import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, Link, useLocation } from "react-router-dom";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SchoolInnerPageBanner from "../../components/SchoolInnerPageBanner";
import useScrollAnimation from "../../constants/useScrollAnimation";
import "./LifePage.css";
import '../../components/LifeSection.css';
import '../../components/PopularEvents.css';

const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const LifePage = () => {
  const location = useLocation();
  const animatedRefs = useRef([]);

  const { school_slug } = useParams();

  /* 🔹 SEPARATE REFS */
  const imageRefs = useRef([]);          // FIX: image-only animation
  const titleRef = useRef(null);
  const sectionRef = useRef(null);

  const [section, setSection] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [newsClippingImages, setNewsClippingImages] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(true);

  /* 🔥 FULLSCREEN MODAL STATE */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalCaption, setModalCaption] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalType, setModalType] = useState("gallery");

  const [eventsVisible, setEventsVisible] = useState(false);
  const eventsRef = useRef(null);

  /* 🔹 VIDEO SECTION STATE */
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoPlayerRef = useRef(null);

  const handlePlayPause = () => {
    if (videoPlayerRef.current) {
      if (isPlaying) {
        videoPlayerRef.current.pause();
        setIsPlaying(false);
      } else {
        videoPlayerRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    animatedRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    scrollToTop();
  }, []);

  /* ---------------- LIFE CONTENT ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    setLoading(true);
    setError("");

    axios
      .get(
        `${baseAPIUrl}api/life_at_tigps/get/${finalSchoolSlug}`
      )
      .then((res) => {
        if (res.data.status === 1) {
          setSection(res.data.data.section);
          setBlocks(res.data.data.blocks || []);
        } else {
          setError("No content available");
        }
      })
      .catch(() => setError("Failed to load content"))
      .finally(() => setLoading(false));
  }, [school_slug]);

  /* ---------- IMAGE ANIMATION ---------- */
  useEffect(() => {
    if (!blocks.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    imageRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [blocks]);

  /* ---------------- GALLERY VISIBILITY  ---------------- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (titleRef.current) observer.observe(titleRef.current);
    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  /* ---------------- RESPONSIVE SLIDER ---------------- */
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setSlidesToShow(w <= 600 ? 1 : w <= 992 ? 2 : 4);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- News clippings (PUBLIC API) ---------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(
        `${baseAPIUrl}admin/life_at_tigps/get_news_clippings/${finalSchoolSlug}`
      )
      .then((res) => {
        if (res.data.status === 1) {
          setNewsClippingImages(res.data.data || []);
        }
      })
      .catch((err) => console.error("News clippings API error", err));
  }, [school_slug]);

  /* ---------- Gallery (PUBLIC API) ---------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(
        `${baseAPIUrl}admin/life_at_tigps/get_gallery/${finalSchoolSlug}`
      )
      .then((res) => {
        if (res.data.status === 1) {
          setGalleryImages(res.data.data || []);
        }
      })
      .catch((err) => console.error("Gallery API error", err));
  }, [school_slug]);

  /* ---------- EVENTS (PUBLIC API) ---------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(
        `${baseAPIUrl}admin/life_at_tigps/get_events/${finalSchoolSlug}`
      )
      .then((res) => {
        console.log("FRONT EVENTS:", res.data);

        if (res.data.status === 1 && Array.isArray(res.data.data)) {
          setEventList(res.data.data);
        } else {
          setEventList([]);
        }
      })
      .catch((err) => {
        console.error("Event API error", err);
        setEventList([]);
      });
  }, [school_slug]);


  // events visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setEventsVisible(true);
            observer.disconnect(); // trigger once
          }
        });
      },
      { threshold: 0.2 }
    );

    if (eventsRef.current) {
      observer.observe(eventsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // For gallery
  const settings = {

    infinite: true,
    speed: 500,
    slidesToShow,
    slidesToScroll: 1,
    autoplay: isVisible,
    autoplaySpeed: 2000,
    pauseOnHover: true,
    arrows: true
  };

  const eventsettings = {

    infinite: true,
    speed: 700,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: isVisible,
    autoplaySpeed: 2000,
    pauseOnHover: true,
    cssEase: 'linear',
    centerMode: false,
    centerPadding: '0px',
    arrows: true   // 🔥 IMPORTANT
  };

  /* ================= BEYOND ACADEMICS CAROUSEL ================= */
  const carouselRef = useRef(null);
  const currentSlideRef = useRef(0);

  const showSlide = (index) => {
    if (!carouselRef.current) return;
    const slides = carouselRef.current.querySelectorAll(".carousel-slide");
    slides.forEach((slide, i) => {
      slide.classList.toggle("active", i === index);
    });
  };

  const nextSlide = () => {
    if (!carouselRef.current) return;
    const slides = carouselRef.current.querySelectorAll(".carousel-slide");
    if (!slides.length) return;

    currentSlideRef.current =
      (currentSlideRef.current + 1) % slides.length;

    showSlide(currentSlideRef.current);
  };

  const prevSlide = () => {
    if (!carouselRef.current) return;
    const slides = carouselRef.current.querySelectorAll(".carousel-slide");
    if (!slides.length) return;

    currentSlideRef.current =
      (currentSlideRef.current - 1 + slides.length) % slides.length;

    showSlide(currentSlideRef.current);
  };

  /* INIT */
  useEffect(() => {
    showSlide(0);
  }, []);

  const [activeIndex, setActiveIndex] = useState(0); // first slide active

  /* AUTO SLIDE */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % blocks.length);
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, []);


  /* 🔥 MODAL FUNCTIONS */

  const openModal = (index, type = "gallery") => {

    const list = type === "gallery" ? galleryImages : newsClippingImages;
    const folder = type === "gallery" ? "gallery" : "news_clipping";

    const img = list[index];
    if (!img) return;

    setCurrentIndex(index);
    setModalType(type);

    setModalImage(
      `${baseAPIUrl}assets/images/${folder}/${img.image_name}`
    );

    setModalCaption(img.image_content || "");

    setIsModalOpen(true);
    document.body.style.overflow = "hidden";

  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = (e) => {
    e.stopPropagation();

    const list = modalType === "gallery" ? galleryImages : newsClippingImages;
    const folder = modalType === "gallery" ? "gallery" : "news_clipping";

    const nextIndex = (currentIndex + 1) % list.length;
    const img = list[nextIndex];

    setCurrentIndex(nextIndex);

    setModalImage(
      `${baseAPIUrl}assets/images/${folder}/${img.image_name}`
    );

    setModalCaption(img.image_content || "");
  };

  const prevImage = (e) => {
    e.stopPropagation();

    const list = modalType === "gallery" ? galleryImages : newsClippingImages;
    const folder = modalType === "gallery" ? "gallery" : "news_clipping";

    const prevIndex = (currentIndex - 1 + list.length) % list.length;
    const img = list[prevIndex];

    setCurrentIndex(prevIndex);

    setModalImage(
      `${baseAPIUrl}assets/images/${folder}/${img.image_name}`
    );

    setModalCaption(img.image_content || "");
  };

  const [beyondHeaderRef, isBeyondHeaderVisible] = useScrollAnimation(0.2);
  const [beyondContentRef, isBeyondContentVisible] = useScrollAnimation(0.2);

  const [galleryHeaderRef, isGalleryHeaderVisible] = useScrollAnimation(0.2);
  const [galleryContentRef, isGalleryContentVisible] = useScrollAnimation(0.2);

  const [newsHeaderRef, isNewsHeaderVisible] = useScrollAnimation(0.2);
  const [newsContentRef, isNewsContentVisible] = useScrollAnimation(0.2);

  const [eventsHeaderRef, isEventsHeaderVisible] = useScrollAnimation(0.2);
  const [eventsContentRef, isEventsContentVisible] = useScrollAnimation(0.2);

  const [videoHeaderRef, isVideoHeaderVisible] = useScrollAnimation(0.2);
  const [videoContentRef, isVideoContentVisible] = useScrollAnimation(0.2);

  return (
    <div>
      {/* Hero Image Section */}
      <SchoolInnerPageBanner />

      {/* School Life page */}

      <div
        id="beyond-academics"
        className="rts-campus rts-section-padding"
        style={{ background: "#DBF6FF", padding: "80px 20px" }}
      >
        <div className="container-custom pb--20">
          <div ref={beyondHeaderRef} className={`section-header ${isBeyondHeaderVisible ? "animate-in" : ""}`}>
            <h2 className="beyond-academics mb-4 text-center section-title-hm">{section?.heading}</h2>
          </div>

          <div ref={beyondContentRef} className={`schools-right ${isBeyondContentVisible ? "animate-in" : ""}`}>
            <p className="beyond-academic-sub text-center mt-2">
              {section?.intro_paragraph}
            </p>

            <div className="carousel-wrapper">
              <div className="carousel-content">
                <div className="carousel-image-wrapper" ref={carouselRef}>
                  {blocks.map((sec, index) => (
                    <div className={`carousel-slide ${index === activeIndex ? "active" : ""}`}>
                      <img
                        src={sec.block_image}
                        alt={sec.block_heading}
                        className="campus-video-img"
                      />
                      <div className="video-overlay-box">
                        <h3 className="overlay-title">{sec.block_heading}</h3>
                        <p className="overlay-text">
                          {sec.block_paragraph}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="carousel-controls">
                <button className="slick-arrow slick-prev" onClick={prevSlide}>‹</button>
                <button className="slick-arrow slick-next" onClick={nextSlide}>›</button>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Gallery SLIDER */}
      <div
        id="gallery"
        className="life-section"
        style={{ background: "#fff", padding: "80px 20px" }}
      >
        <div className="container-custom">
          <div ref={galleryHeaderRef} className={`section-header ${isGalleryHeaderVisible ? "animate-in" : ""}`}>
            <h2 className="life-title section-title-hm">
              GALLERY
            </h2>
          </div>

          <div ref={galleryContentRef} className={`schools-right ${isGalleryContentVisible ? "animate-in" : ""}`}>
            {galleryImages.length > 0 ? (
              <Slider {...settings} className="life-slider">
                {galleryImages.map((img, i) => (
                  <div key={img.id || i} className="life-slide">
                    <div className="life-image">

                      <img
                        src={`${baseAPIUrl}assets/images/gallery/${img.image_name}`}
                        alt="Gallery"
                        style={{ cursor: "pointer" }}
                        onClick={() => openModal(i, "gallery")}
                      />

                      {img.image_content && (
                        <div
                          className="life-overlay"
                          onClick={() => openModal(i, "gallery")}
                        >
                          <p className="life-image-title">{img.image_content}</p>
                        </div>
                      )}

                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <p style={{ textAlign: "center", color: "#1D293D" }}>
                No gallery images available
              </p>
            )}
          </div>
        </div>
      </div>


      {/* Latest News & Updates SLIDER */}
      <div id="news-updates" className="events-section" style={{ padding: '80px 20px', background: '#DBF6FF' }}>
        <div className="container-custom">
          <div ref={newsHeaderRef} className={`section-header ${isNewsHeaderVisible ? "animate-in" : ""}`}>
            {/* Latest News & Updates SLIDER HEADING */}
            <h2 className="news-title section-title-hm" style={{ color: '#1D293D',fontFamily: 'Lora',fontWeight:'Bold',fontSize: '48px', }}>LATEST NEWS & UPDATES</h2>
          </div>

          <div ref={newsContentRef} className={`schools-right ${isNewsContentVisible ? "animate-in" : ""}`}>
            {newsClippingImages.length > 0 ? (
              <Slider {...settings} className="news-slider">
                {newsClippingImages.map((img, i) => (
                  <div key={img.id || i} className="news-slide">
                    <div className="life-image">
                      <img
                        src={`${baseAPIUrl}assets/images/news_clipping/${img.image_name}`}
                        alt="News"
                        style={{ cursor: "pointer" }}
                        onClick={() => openModal(i, "news")}
                      />
                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <p style={{ textAlign: "center", color: "#fff" }}>
                No News & updates available
              </p>
            )}
          </div>

        </div>
      </div>

      {/* Events HEADING */}
      <div id="celebratory-events"
        className="events-section"
        style={{ padding: '80px 20px', background: 'rgb(248, 249, 250)' }}
      >
        <div className="container-custom">
          <div ref={eventsHeaderRef} className={`section-header ${isEventsHeaderVisible ? "animate-in" : ""}`}>
            <h2 className="events-title section-title-hm">CELEBRATORY EVENTS</h2>
          </div>

          <div ref={eventsContentRef} className={`schools-right ${isEventsContentVisible ? "animate-in" : ""}`}>
            {eventList.length > 0 ? (
              <Slider {...eventsettings} className="events-slider">
                {eventList.map((event) => (
                  <div key={event.id} className="event-slide">
                    <div className="event-card">

                      <div className="event-image">
                        <img
                          src={
                            event.images?.[0]?.image_name
                              ? `${baseAPIUrl}assets/images/events/${event.images[0].image_name}`
                              : "/pictures/default-event.jpg"
                          }
                          alt={event.event_name}
                          onError={(e) =>
                            (e.target.src = "/pictures/default-event.jpg")
                          }
                        />
                      </div>

                      <div className="event-content">
                        <h4 style={{ fontFamily: "Lora", fontSize: "1.2rem", color: '#1D293D' }}>{event.event_name}</h4>

                        <Link
                          to={`/events/${event.id}`}
                          className="event-btn"
                        >
                          Read more
                        </Link>
                      </div>

                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <p style={{ textAlign: "center" }}>No events available</p>
            )}
          </div>
        </div>
      </div>

      {/* Video section */}
      <div
        id="video-section"
        className="events-section"
        style={{ padding: "80px 20px", background: "#DBF6FF" }}
      >
        <div className="container-custom" style={{ textAlign: "center" }}>
          <div ref={videoHeaderRef} className={`section-header ${isVideoHeaderVisible ? "animate-in" : ""}`}>
            <h2 className="news-title section-title-hm" style={{ color: "#1D293D",fontFamily:'Lora',fontWeight:'Bold', }}>
              YOGA DAY CELEBRATION
            </h2>
          </div>

          <div ref={videoContentRef} className={`schools-right ${isVideoContentVisible ? "animate-in" : ""}`}>
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                position: "relative",
                maxWidth: "800px",
                margin: "0 auto",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                background: "#000",
                aspectRatio: "16 / 9",
              }}
            >
              <video
                ref={videoPlayerRef}
                playsInline
                preload="metadata"
                poster={`${baseAPIUrl}assets/images/life_at_tigps/1776169318_YOGA (1).jpg`}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: "12px",
                  cursor: "pointer",
                }}
                onClick={handlePlayPause}
              >
                <source
                  src={`${baseAPIUrl}assets/images/videos/yoga-day-video.mp4`}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>

              {/* Custom Play/Pause Overlay Button */}
              <div
                onClick={handlePlayPause}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isPlaying ? "transparent" : "rgba(0, 0, 0, 0.35)",
                  transition: "background 0.3s ease",
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: isPlaying && !isHovered ? "none" : "auto",
                }}
              >
                {(!isPlaying || isHovered) && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      background: "#1D293D",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.5)",
                      color: "#fff",
                      transition: "transform 0.2s ease, opacity 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  >
                    {isPlaying ? (
                      /* Pause Icon */
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                      </svg>
                    ) : (
                      /* Play Icon */
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style={{ transform: "translateX(2px)" }}>
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fullscreen-modal" onClick={closeModal}>

          <span className="modal-close" onClick={closeModal}>
            &times;
          </span>

          <button className="modal-prev" onClick={prevImage}>
            ‹
          </button>

          <div
            className="fullscreen-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={modalImage} alt="Full View" />

            {modalCaption && (
              <p className="modal-caption">{modalCaption}</p>
            )}
          </div>

          <button className="modal-next" onClick={nextImage}>
            ›
          </button>

        </div>
      )}

    </div>
  );
};

export default LifePage;